import {
  getEditorScrollTarget,
  getPlainToolbarAppendHost,
} from '@/components/tiptap/shared/text-menu-scroll-target';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { isEditorLive, pickLastNonEmptyClientRect } from '@heidi/editor/core';
import {
  getDialogContainer,
  TextMenuWrapper as PackagedTextMenuWrapper,
  Surface,
} from '@heidi/editor/ui';
import { useLatestRef } from '@heidi/utils';
import { type EditorState } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { posToDOMRect, type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface TextMenuWrapperProps {
  editor: Editor;
  shouldShow: (props: { view: EditorView; state: EditorState; from: number }) => boolean;
  children: ReactNode;
  /**
   * Optional panel rendered below the toolbar row. When set, the menu becomes
   * a vertical panel (toolbar on top, footer beneath) instead of the plain
   * horizontal toolbar. Used by the selection → Ask Heidi actions.
   */
  footer?: ReactNode;
  /** Called only after the footer BubbleMenu's full visibility predicate passes. */
  onShow?: () => void;
  /** Called on every close — the predicate failing and the panel unmounting. */
  onHide?: () => void;
}

/**
 * Zero-width anchor at the RIGHT EDGE of the selection's last line — not the
 * full multi-line bounding box. `Range.getBoundingClientRect()` for a wrapping
 * / multi-line selection is as wide as the text column, which parked the menu
 * at the column edge. `getClientRects()` keeps it next to the end of the
 * highlighted text. Deliberately NOT from `window.getSelection()`: the native
 * selection can collapse while the ProseMirror selection (and highlight)
 * persists, which previously teleported the menu to the editor rect.
 *
 * Right-side placement still cannot cover that final line; vertical flip
 * fallbacks sit below / above it.
 */
const getSelectionEdgeAnchorRect = (editor: Editor): DOMRect | null => {
  const { empty, from, to } = editor.state.selection;
  if (empty) return null;
  try {
    const start = editor.view.domAtPos(from);
    const end = editor.view.domAtPos(to);
    const range = editor.view.dom.ownerDocument.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);

    let lineRect = pickLastNonEmptyClientRect(range.getClientRects());
    if (!lineRect) {
      const bbox = range.getBoundingClientRect();
      if (bbox.width === 0 && bbox.height === 0) return null;
      lineRect = bbox;
    }
    return new DOMRect(lineRect.right, lineRect.top, 0, lineRect.height);
  } catch {
    return null;
  }
};

/** Anchor position captured relative to the editor container (scroll-safe). */
interface PinnedAnchor {
  from: number;
  to: number;
  relLeft: number;
  relTop: number;
  height: number;
}

/**
 * App wrapper over the packaged bubble-menu frame (6.5/6.6 wave B-1 — the
 * 6.1a shell pattern for components): the frame moved to `@heidi/editor/ui`
 * with visibility inverted to a `suppressed` prop; this path keeps the exact
 * legacy signature and wires the app's mobile breakpoint, so all four twin
 * text menus keep their specifier and behavior.
 *
 * When `footer` is set (selection Ask Heidi panel), the packaged frame is too
 * narrow: we need a pinned right-edge anchor, body append, and a vertical
 * Surface instead of the horizontal toolbar. That product path stays here.
 */
export const TextMenuWrapper = ({
  editor,
  shouldShow,
  children,
  footer,
  onShow,
  onHide,
}: TextMenuWrapperProps) => {
  const isMobile = useIsMobile();
  const [scrollTarget, setScrollTarget] = useState<HTMLElement | Window | null>(null);
  const [focusGateOpen, setFocusGateOpen] = useState(true);
  const menuSurfaceRef = useRef<HTMLDivElement | null>(null);

  // BubbleMenu registers its plugin once per editor mount and snapshots
  // `options.scrollTarget` at registration time — resolve after view.dom is
  // connected so we listen on the note scroller, not window.
  useEffect(() => {
    if (!isEditorLive(editor, 'text-menu-base.scrollTarget')) {
      return;
    }

    const resolveScrollTarget = () => {
      // Also runs from a rAF that can land after the editor is destroyed.
      if (!isEditorLive(editor, 'text-menu-base.resolveScrollTarget')) {
        return;
      }
      if (!editor.view.dom.isConnected) {
        return;
      }
      setScrollTarget(getEditorScrollTarget(editor));
    };

    resolveScrollTarget();
    editor.on('create', resolveScrollTarget);
    const frame = requestAnimationFrame(resolveScrollTarget);

    return () => {
      editor.off('create', resolveScrollTarget);
      cancelAnimationFrame(frame);
    };
  }, [editor]);

  // Body append breaks BubbleMenu blur hide (parentNode is document.body), so
  // unmount the panel content when focus leaves the editor and menu surface.
  // BubbleMenu itself must stay mounted: unmounting it unregisters its plugin,
  // and the plugin reconfigure destroys every plugin view — including an open
  // slash-command menu the user is clicking into.
  useEffect(() => {
    if (!footer || !isEditorLive(editor, 'text-menu-base.focusGate')) {
      return;
    }

    const isFocusInsideSurface = (target: Node | null): boolean => {
      if (!target) {
        return false;
      }
      if (editor.view.dom.contains(target)) {
        return true;
      }
      if (menuSurfaceRef.current?.contains(target)) {
        return true;
      }
      return false;
    };

    let pendingFrame: number | null = null;

    const dismissOnFocusLeave = () => {
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        // The editor can be destroyed between the focus change and this frame.
        if (!isEditorLive(editor, 'text-menu-base.focusGateFrame')) {
          return;
        }
        if (isFocusInsideSurface(document.activeElement)) {
          return;
        }
        setFocusGateOpen(false);
      });
    };

    const reopenOnEditorFocus = () => setFocusGateOpen(true);

    editor.on('blur', dismissOnFocusLeave);
    editor.on('focus', reopenOnEditorFocus);
    document.addEventListener('focusin', dismissOnFocusLeave);

    return () => {
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
      editor.off('blur', dismissOnFocusLeave);
      editor.off('focus', reopenOnEditorFocus);
      document.removeEventListener('focusin', dismissOnFocusLeave);
    };
  }, [editor, footer]);

  // The plugin makes its host a Tab stop and, appended to <body>, never hides
  // it on an outside blur — so the empty host left by a closed gate must not
  // stay focusable.
  useEffect(() => {
    const menuHost = menuSurfaceRef.current;
    if (!footer || !menuHost) return;
    menuHost.tabIndex = focusGateOpen ? 0 : -1;
  }, [footer, focusGateOpen, scrollTarget]);

  // Pin the panel's anchor for the lifetime of one selection range: formatting
  // actions (bullet list, bold, …) reflow the text and move the selection's
  // bounding box, which made the menu jump around mid-interaction. The anchor
  // is cached relative to the editor container the first time the menu shows,
  // so it stays put through reflows but still tracks scrolling; it resets
  // when the selection collapses or the highlighted range changes.
  const pinnedAnchorRef = useRef<PinnedAnchor | null>(null);

  useEffect(() => {
    if (!footer) return;
    const onSelectionUpdate = () => {
      const { empty, from, to } = editor.state.selection;
      if (empty) {
        pinnedAnchorRef.current = null;
        return;
      }
      const pinned = pinnedAnchorRef.current;
      if (pinned && (pinned.from !== from || pinned.to !== to)) {
        pinnedAnchorRef.current = null;
      }
    };
    editor.on('selectionUpdate', onSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', onSelectionUpdate);
    };
  }, [editor, footer]);

  const getPinnedAnchorRect = (): DOMRect | null => {
    const editorRect = editor.view.dom.getBoundingClientRect();
    const pinned = pinnedAnchorRef.current;
    if (pinned) {
      return new DOMRect(
        editorRect.left + pinned.relLeft,
        editorRect.top + pinned.relTop,
        0,
        pinned.height
      );
    }
    const rect = getSelectionEdgeAnchorRect(editor);
    if (!rect) return null;
    const { from, to } = editor.state.selection;
    pinnedAnchorRef.current = {
      from,
      to,
      relLeft: rect.left - editorRect.left,
      relTop: rect.top - editorRect.top,
      height: rect.height,
    };
    return rect;
  };

  // Floating UI reads this lazily, so it can run after the editor is destroyed.
  const resolveAnchorRect = (): DOMRect => {
    if (!isEditorLive(editor, 'text-menu-base.resolveAnchorRect')) return new DOMRect();
    return (
      getPinnedAnchorRect() ??
      posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to)
    );
  };

  // The panel unmounts without the predicate ever reporting false.
  const isFooterPanelMounted = Boolean(footer) && scrollTarget !== null && focusGateOpen;
  // BubbleMenu snapshots `shouldShow` once per plugin registration and now
  // stays mounted for the editor's lifetime, so the predicate reads these.
  const latestPredicateInputsRef = useLatestRef({ isMobile, shouldShow, onShow, onHide });
  useEffect(() => {
    if (!footer || isFooterPanelMounted) return;
    latestPredicateInputsRef.current.onHide?.();
  }, [footer, isFooterPanelMounted, latestPredicateInputsRef]);

  if (!footer) {
    // Note scrollers carry `contain: layout` (SCR-6527 / SCR-7448), and an
    // opacity < 1 ancestor (the offline gate) is a second stacking context.
    // Append outside both, inside the isolate that also parents the gutter.
    // BubbleMenu reads `scrollTarget` once at registration, so the key remounts
    // it whenever the resolved kind changes (pending → window → scroller).
    const uncontainedHost =
      scrollTarget instanceof HTMLElement
        ? getPlainToolbarAppendHost(scrollTarget)
        : null;
    const scrollTargetKind =
      scrollTarget instanceof HTMLElement
        ? 'element'
        : scrollTarget
          ? 'window'
          : 'pending';
    return (
      <PackagedTextMenuWrapper
        key={`scroll-target-${scrollTargetKind}`}
        editor={editor}
        shouldShow={shouldShow}
        suppressed={isMobile}
        scrollTarget={scrollTarget ?? undefined}
        appendTo={() => getDialogContainer(editor, { fallbackElement: uncontainedHost })}
      >
        {children}
      </PackagedTextMenuWrapper>
    );
  }

  if (!scrollTarget) {
    return null;
  }

  return (
    <BubbleMenu
      // Distinct from TableMenu: this path unmounts on blur, and TipTap
      // unregisterPlugin prefix-matches the key.
      pluginKey="textMenu"
      ref={menuSurfaceRef}
      shouldShow={({ view, state, from, element }) => {
        const latest = latestPredicateInputsRef.current;
        const isChildOfMenu = element.contains(document.activeElement);
        const isVisible =
          !latest.isMobile &&
          (view.hasFocus() || isChildOfMenu) &&
          latest.shouldShow({ view, state, from });
        if (isVisible) {
          latest.onShow?.();
        } else {
          latest.onHide?.();
        }
        return isVisible;
      }}
      editor={editor}
      // The AI-actions panel may extend over neighbouring chrome, so it sits
      // on the design-system dropdown layer. TipTap positions the host it
      // appends but forwards `className` to an unpositioned inner div, where
      // a bare z-index is inert; `relative` makes the z-50 apply.
      className="relative z-50"
      // The AI-actions panel appends to <body> (outside the note column's
      // stacking context) so it — and its inline Rewrite / More options
      // sub-flyouts — paint above the side panel. Both still prefer an open
      // dialog container.
      appendTo={() => getDialogContainer(editor, { fallbackToBody: true })}
      // TipTap's resize/scroll timers can resolve this after the editor is destroyed.
      getReferencedVirtualElement={() => ({
        contextElement: isEditorLive(editor, 'text-menu-base.virtualElement')
          ? editor.view.dom
          : undefined,
        getBoundingClientRect: resolveAnchorRect,
      })}
      options={{
        // BubbleMenu defaults scrollTarget to window; the note scrolls in a
        // nested overflow container, so listen there to recompute position.
        scrollTarget,
        // The AI-actions panel is much taller than the plain toolbar —
        // sit it 8px right of the last selected line's end, top-aligned.
        // Only strictly-outside fallbacks are allowed: below that line,
        // then above it. Never left (that's the highlight) and shift
        // can only slide it along the safe right edge.
        placement: 'right-start',
        offset: 8,
        flip: {
          fallbackPlacements: ['bottom-end', 'top-end'],
          padding: 8,
        },
        shift: { padding: 8 },
      }}
    >
      {focusGateOpen && (
        <Surface
          data-selection-bubble-menu
          className="flex min-w-52 flex-col"
          onMouseDown={(e) => {
            // Keep the editor selection alive while interacting with the menu
            e.preventDefault();
          }}
        >
          <div className="flex flex-col gap-0.5 p-2 leading-none text-text-primary">
            {children}
          </div>
          {footer}
        </Surface>
      )}
    </BubbleMenu>
  );
};
